import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Edit, CheckCircle, XCircle, Shield } from 'lucide-react';
import { investorRolesAPI } from '../../../services/api';
import { custodianRolesAPI } from '../../../services/custodianRolesAPI';
import { facilityAgentRolesAPI } from '../../../services/facilityAgentRolesAPI';
import { issuerRolesAPI } from '../../../services/issuerRolesAPI';
import { superAdminRolesAPI } from '../../../services/superAdminRolesAPI';
import { primarySubscriberRolesAPI } from '../../../services/primarySubscriberRolesAPI';
import { getRoleModelForUser } from '../../../utils/roleModelMapping';

const formatPermissionName = (permission) => {
  const permissionLabels = {
    'access_dashboard': 'Dashboard',
    'access_create_bond': 'Create Bond',
    'access_bond_management': 'Bond Management',
    'access_documents': 'Documents',
    'access_announcements': 'Announcements',
    'access_sub_accounts': 'Sub Accounts',
    'access_role_management': 'Role Management',
    'access_audit_logs': 'Audit Logs',
    'access_subscription_management': 'Subscription Management',
    'access_create_sub_agreement': 'Create Sub Agreement',
    'access_sub_agreement_management': 'Sub Agreement Management',
    'access_bond_applications': 'Bond Applications',
    'access_bond_holdings': 'Bond Holdings',
    'access_issuance_payment_management': 'Issuance Payment Management',
    'access_coupon_management': 'Coupon Management',
    'access_maturity_management': 'Maturity Management',
    'access_token_holder': 'Bond Holder',
    'access_create_loc': 'Create LOC',
    'access_loc_management': 'LOC Management',
    'access_submit_sub_agreement': 'Submit Sub Agreement',
    'access_receive_bond_token': 'Receive Bond Token',
    'access_bond_token_allocation': 'Bond Token Allocation',
    'access_sub_investors': 'Sub Investors',
    'access_sub_investor_loc': 'Sub Investor LOC',
    'access_wallet_management': 'Wallet Management',
    'access_reports': 'Reports',
    'access_user_management': 'User Management',
    'access_submit_loc': 'Submit LOC',
    'access_assigned_bonds': 'Assigned Bonds',
    'access_bond_holders': 'Bond Holders'
  };

  return permissionLabels[permission] || permission
    .replace(/^access_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatPermissionsList = (permissions) => {
  if (!permissions || permissions.length === 0) return 'No permissions';
  return permissions.map(formatPermissionName).join(', ');
};

const EditSubAccountModal = ({
  isOpen,
  onClose,
  selectedSubAccount,
  getSubAccountDisplayName,
  getStatusBadge,
  onActivate,
  onDeactivate,
  onUpdate,
  isActivating,
  isDeactivating,
  isUpdating,
  currentUserRole
}) => {
  const [formData, setFormData] = useState({
    firstName: selectedSubAccount?.personalInfo?.firstName || '',
    lastName: selectedSubAccount?.personalInfo?.lastName || '',
    phoneNumber: selectedSubAccount?.personalInfo?.phoneNumber || '',
    assignedRoleId: selectedSubAccount?.assignedRoleId?._id || selectedSubAccount?.assignedRoleId || ''
  });
  const [errors, setErrors] = useState({});

  // Fetch available roles for investor, custodian, facility agent, or issuer
  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useQuery({
    queryKey: ['roles', currentUserRole],
    queryFn: async () => {
      if (currentUserRole === 'investor') {
        const response = await investorRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'custodian') {
        const response = await custodianRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'facility_agent') {
        const response = await facilityAgentRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'issuer') {
        const response = await issuerRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'super_admin') {
        const response = await superAdminRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'primary_subscriber') {
        const response = await primarySubscriberRolesAPI.getRoles();
        return response;
      }
      return Promise.resolve({ data: { data: { roles: [] } } });
    },
    select: (data) => {
      // Handle different possible response structures
      let roles = [];

      // Check for axios response structure first
      if (data?.data?.data?.roles) {
        // Axios response: { data: { success, message, data: { roles: [...] } } }
        roles = data.data.data.roles;
      } else if (data?.data?.roles) {
        // Standard response: { success, message, data: { roles: [...] } }
        roles = data.data.roles;
      } else if (data?.roles) {
        // Direct response: { roles: [...] }
        roles = data.roles;
      } else if (Array.isArray(data)) {
        // Array response: [...]
        roles = data;
      }

      return roles;
    },
    enabled: (currentUserRole === 'investor' || currentUserRole === 'custodian' || currentUserRole === 'facility_agent' || currentUserRole === 'issuer' || currentUserRole === 'super_admin' || currentUserRole === 'primary_subscriber') && isOpen
  });



  // Update form data when selectedSubAccount changes
  React.useEffect(() => {
    if (selectedSubAccount) {
      setFormData({
        firstName: selectedSubAccount.personalInfo?.firstName || '',
        lastName: selectedSubAccount.personalInfo?.lastName || '',
        phoneNumber: selectedSubAccount.personalInfo?.phoneNumber || '',
        assignedRoleId: selectedSubAccount.assignedRoleId?._id || selectedSubAccount.assignedRoleId || ''
      });
    }
  }, [selectedSubAccount]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Phone number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    // Role validation
    if ((currentUserRole === 'investor' || currentUserRole === 'custodian' || currentUserRole === 'facility_agent' || currentUserRole === 'issuer' || currentUserRole === 'super_admin' || currentUserRole === 'primary_subscriber') && !formData.assignedRoleId) {
      newErrors.assignedRoleId = 'Please select a role for the sub-account';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare the data for sub-account update
    const updateData = {
      personalInfo: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber
      },
      assignedRoleId: formData.assignedRoleId || undefined,
      assignedRoleModel: formData.assignedRoleId ? getRoleModelForUser(currentUserRole) : undefined
    };



    onUpdate(selectedSubAccount._id, updateData);
  };

  const handleActivate = () => {
    onActivate(selectedSubAccount._id);
  };

  const handleDeactivate = () => {
    onDeactivate(selectedSubAccount._id);
  };

  if (!isOpen || !selectedSubAccount) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-light-gray">
          <div className="flex items-center">
            <Edit className="w-6 h-6 mr-3 text-primary-600" />
            <h3 className="text-lg font-medium text-gray-900">
              Edit Sub Account
            </h3>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: '#9ca3af' }}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Sub Account Info */}
          <div className="p-4 mb-6 rounded-lg bg-secondary-50">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-12 h-12">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-100">
                  <span className="text-lg font-medium text-primary-600">
                    {getSubAccountDisplayName(selectedSubAccount).charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <div className="text-lg font-medium text-secondary-900">
                  {getSubAccountDisplayName(selectedSubAccount)}
                </div>
                <div className="text-sm text-secondary-500">
                  {selectedSubAccount.email}
                </div>
                <div className="flex items-center mt-1">
                  {getStatusBadge(selectedSubAccount)}
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block mb-1 text-sm font-medium text-dark-gray">
                  First Name <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`input border-light-gray ${errors.firstName ? 'border-error-500' : ''}`}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-error-600">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block mb-1 text-sm font-medium text-dark-gray">
                  Last Name <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`input border-light-gray ${errors.lastName ? 'border-error-500' : ''}`}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-error-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block mb-1 text-sm font-medium text-dark-gray">
                Phone Number <span className="required-asterisk">*</span>
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`input border-light-gray ${errors.phoneNumber ? 'border-error-500' : ''}`}
                placeholder="Enter phone number"
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-error-600">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Role Assignment for Investor, Custodian, Facility Agent, Issuer, or Super Admin */}
            {(currentUserRole === 'investor' || currentUserRole === 'custodian' || currentUserRole === 'facility_agent' || currentUserRole === 'issuer' || currentUserRole === 'super_admin' || currentUserRole === 'primary_subscriber') && (
              <div>
                <label htmlFor="assignedRoleId" className="block mb-1 text-sm font-medium text-dark-gray">
                  Assign Role <span className="required-asterisk">*</span>
                </label>
                <select
                  id="assignedRoleId"
                  name="assignedRoleId"
                  value={formData.assignedRoleId}
                  onChange={handleInputChange}
                  className={`input border-light-gray ${errors.assignedRoleId ? 'border-error-500' : ''}`}
                >
                  <option value="">Select a role</option>
                  {rolesLoading && <option value="" disabled>Loading roles...</option>}
                  {rolesError && <option value="" disabled>Error loading roles</option>}
                  {rolesData && rolesData.length > 0 ? (
                    rolesData.map((role) => (
                      <option key={role._id} value={role._id}>
                        {role.name} ({role.permissions.length} permissions)
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No roles available</option>
                  )}
                </select>
                {errors.assignedRoleId && (
                  <p className="mt-1 text-sm text-error-600">{errors.assignedRoleId}</p>
                )}
                {formData.assignedRoleId && (
                  <div className="p-3 mt-2 border rounded-md bg-info-50 border-info-200">
                    <div className="flex items-start">
                      <Shield className="h-4 w-4 text-info-400 mt-0.5 mr-2 flex-shrink-0" />
                      <div className="text-sm text-info-700">
                        <p className="font-medium">Selected Role Permissions:</p>
                        <p className="mt-1 text-xs">
                          {formatPermissionsList(rolesData?.find(r => r._id === formData.assignedRoleId)?.permissions)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Read-only fields */}
            <div>
              <label className="block mb-1 text-sm font-medium text-dark-gray">
                Email Address
              </label>
              <input
                type="email"
                value={selectedSubAccount.email}
                className="cursor-not-allowed input border-light-gray bg-secondary-50 text-secondary-500"
                readOnly
              />
              <p className="mt-1 text-xs text-gray-400">
                Email address cannot be changed
              </p>
            </div>

            {/* Info Box */}
            <div className="p-4 border rounded-md bg-info-50 border-info-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Edit className="w-5 h-5 text-info-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-info-800">
                    Sub Account Information
                  </h3>
                  <div className="mt-2 text-sm text-info-700">
                    <p>• This sub-account shares the same corporate information as your account</p>
                    <p>• You can manage their specific permissions through Role Management</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end pt-4 space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdating}
                className="btn-primary"
              >
                {isUpdating ? (
                  <div className="flex items-center">
                    <div className="w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                    Updating...
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditSubAccountModal; 